/**
 * Credits Service for Client-Side Operations
 * Handles all credit management using Supabase directly
 */

import type { QueryClient } from "@tanstack/react-query";
import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";

/** Internal accounts: AI usage does not deduct credits or block on zero balance. */
const UNLIMITED_AI_CREDITS_EMAILS = new Set(
  [
    "yourbusinessonai@gmail.com",
    "james@wbgr.com",
    "beth@trm5.com",
    "merrianne@live.com",
    "merrianne@live.co.uk",
    "pretty2427@duck.com",
  ].map((e) => e.toLowerCase())
);

export function isUnlimitedAiCreditsEmail(email?: string | null): boolean {
  const e = email?.trim().toLowerCase();
  return !!e && UNLIMITED_AI_CREDITS_EMAILS.has(e);
}

async function getSessionLoginEmail(): Promise<string | null> {
  if (!isSupabaseConfigured) return null;
  const session = await getCurrentSession();
  const u = session?.user;
  if (!u) return null;
  const fromEmail = u.email?.trim();
  if (fromEmail) return fromEmail.toLowerCase();
  const meta = u.user_metadata as Record<string, unknown> | undefined;
  const username = typeof meta?.username === "string" ? meta.username.trim() : "";
  if (username.includes("@")) return username.toLowerCase();
  return null;
}
import { PENDING_SIGNUP_COUPON_KEY } from "@/lib/couponConstants";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { STRIPE_PUBLISHABLE_KEY } from "@/constants/stripe";

export interface UserCredits {
  id: string;
  userId: string;
  balance: string;
  totalEarned: string;
  totalSpent: string;
  lastResetAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreditTransaction {
  id: string;
  userId: string;
  type: string;
  amount: string;
  balanceBefore: string;
  balanceAfter: string;
  reason: string;
  metadata: any;
  createdAt: string;
}

export interface CreditsOverview {
  balance: number;
  totalEarned: number;
  totalSpent: number;
  monthlyAllowance: number;
  percentageUsed: number;
  transactions: CreditTransaction[];
  usageByCategory: Record<string, number>;
  monthlyUsage: Array<{ date: string; credits: number }>;
  isLowBalance: boolean;
  isDepleted: boolean;
  /** When true, AI chat is not gated or charged against balance (internal accounts). */
  unlimitedAiCredits?: boolean;
}

export interface CreditsStatus {
  balance: number;
  monthlyAllowance: number;
  isLowBalance: boolean;
  isDepleted: boolean;
  unlimitedAiCredits?: boolean;
}

export interface BillingSubscription {
  tierName: string;
  monthlyCredits: number;
  nextBillingDate: string | null;
  isCancelled: boolean;
  cancelAtPeriodEnd: boolean;
  subscription: {
    status: string;
  } | null;
  plan: {
    name: string;
    displayName: string;
    price: string;
  } | null;
}

export interface PricingTier {
  id?: string;
  name: string;
  price: number;
  priceDisplay: string;
  monthlyCredits: number;
  features: string[];
  popular: boolean;
  description?: string;
  currency?: string;
  interval?: string;
  intervalCount?: number;
  trialDays?: number;
  maxDevices?: number;
  numberOfTokens?: number;
  status?: string;
}

export interface BillingTiersResponse {
  tiers: PricingTier[];
}

// Helper function to format credits
function formatCredits(num: number): string {
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + "K";
  }
  return num.toString();
}

class CreditsService {
  private async hasUnlimitedAiCredits(): Promise<boolean> {
    const email = await getSessionLoginEmail();
    return isUnlimitedAiCreditsEmail(email);
  }

  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated. Please log in.");
    }
    return session.user.id;
  }

  private mapCreditsFromDb(row: any): UserCredits {
    return {
      id: row.id,
      userId: row.user_id,
      balance: row.balance,
      totalEarned: row.total_earned,
      totalSpent: row.total_spent,
      lastResetAt: row.last_reset_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }

  private mapTransactionFromDb(row: any): CreditTransaction {
    return {
      id: row.id,
      userId: row.user_id,
      type: row.type,
      amount: row.amount,
      balanceBefore: row.balance_before,
      balanceAfter: row.balance_after,
      reason: row.reason,
      metadata: row.metadata,
      createdAt: row.created_at,
    };
  }

  async getUserCredits(): Promise<UserCredits | null> {
    const userId = await this.getCurrentUserId();
    
    const { data, error } = await supabase
      .from("user_credits")
      .select("id, user_id, balance, total_earned, total_spent, last_reset_at, created_at, updated_at")
      .eq("user_id", userId)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        return null;
      }
      console.error("Error fetching user credits:", error);
      throw new Error(`Failed to fetch credits: ${error.message}`);
    }

    return data ? this.mapCreditsFromDb(data) : null;
  }

  async createUserCredits(initialBalance: number = 1000): Promise<UserCredits> {
    const userId = await this.getCurrentUserId();
    
    const { data, error } = await supabase
      .from("user_credits")
      .insert({
        user_id: userId,
        balance: String(initialBalance),
        total_earned: String(initialBalance),
        total_spent: "0",
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating user credits:", error);
      throw new Error(`Failed to create credits: ${error.message}`);
    }

    // Record welcome credit grant
    if (initialBalance > 0) {
      await supabase.from("credit_transactions").insert({
        user_id: userId,
        type: "credit",
        amount: String(initialBalance),
        balance_before: "0",
        balance_after: String(initialBalance),
        reason: "welcome_bonus",
        metadata: { source: "signup", description: "Free starter credits" },
      });
    }

    return this.mapCreditsFromDb(data);
  }

  async getOrCreateUserCredits(): Promise<UserCredits> {
    let credits = await this.getUserCredits();
    if (!credits) {
      credits = await this.createUserCredits();
    }
    return credits;
  }

  async updateCreditsBalance(newBalance: number): Promise<UserCredits> {
    const userId = await this.getCurrentUserId();
    
    const { data, error } = await supabase
      .from("user_credits")
      .update({
        balance: String(newBalance),
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      console.error("Error updating credits:", error);
      throw new Error(`Failed to update credits: ${error.message}`);
    }

    return this.mapCreditsFromDb(data);
  }

  async deductCredits(amount: number, reason: string, metadata?: any): Promise<UserCredits> {
    const userId = await this.getCurrentUserId();
    const credits = await this.getOrCreateUserCredits();
    const currentBalance = parseInt(credits.balance, 10) || 0;
    const actualDeduction = Math.min(amount, currentBalance);
    if (actualDeduction <= 0) {
      return credits;
    }
    const newBalance = currentBalance - actualDeduction;
    const newTotalSpent = parseInt(credits.totalSpent, 10) + actualDeduction;

    const { data: updatedCredits, error: updateError } = await supabase
      .from("user_credits")
      .update({
        balance: String(newBalance),
        total_spent: String(newTotalSpent),
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .select()
      .single();

    if (updateError) {
      console.error("Error deducting credits:", updateError);
      throw new Error(`Failed to deduct credits: ${updateError.message}`);
    }

    await supabase.from("credit_transactions").insert({
      user_id: userId,
      type: "debit",
      amount: String(-actualDeduction),
      balance_before: String(currentBalance),
      balance_after: String(newBalance),
      reason,
      metadata: metadata || {},
    });

    return this.mapCreditsFromDb(updatedCredits);
  }

  async addCredits(amount: number, reason: string, metadata?: any): Promise<UserCredits> {
    const userId = await this.getCurrentUserId();
    const credits = await this.getOrCreateUserCredits();
    const currentBalance = parseInt(credits.balance);
    const newBalance = currentBalance + amount;
    const newTotalEarned = parseInt(credits.totalEarned) + amount;

    const { data: updatedCredits, error: updateError } = await supabase
      .from("user_credits")
      .update({
        balance: String(newBalance),
        total_earned: String(newTotalEarned),
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .select()
      .single();

    if (updateError) {
      console.error("Error adding credits:", updateError);
      throw new Error(`Failed to add credits: ${updateError.message}`);
    }

    await supabase.from("credit_transactions").insert({
      user_id: userId,
      type: "credit",
      amount: String(amount),
      balance_before: String(currentBalance),
      balance_after: String(newBalance),
      reason,
      metadata: metadata || {},
    });

    return this.mapCreditsFromDb(updatedCredits);
  }

  /**
   * Calculate and deduct credits for an AI chat response based on tokens and pricing
   */
  async deductCreditsForAIUsage(params: {
    promptTokens: number;
    completionTokens: number;
    promptPrice: string | number;  // Price per token in USD
    completionPrice: string | number;  // Price per token in USD
    model: string;
    profitMultiplier?: number;
  }): Promise<{ creditsDeducted: number; userCredits: UserCredits; hadEnoughBalance: boolean }> {
    if (await this.hasUnlimitedAiCredits()) {
      const userCredits = await this.getOrCreateUserCredits();
      return { creditsDeducted: 0, userCredits, hadEnoughBalance: true };
    }

    const {
      promptTokens,
      completionTokens,
      promptPrice,
      completionPrice,
      model,
      profitMultiplier = 3
    } = params;

    // Parse prices
    const promptPriceNum = typeof promptPrice === 'string' ? parseFloat(promptPrice) : promptPrice;
    const completionPriceNum = typeof completionPrice === 'string' ? parseFloat(completionPrice) : completionPrice;

    // Calculate actual cost in USD
    const inputCost = promptTokens * promptPriceNum;
    const outputCost = completionTokens * completionPriceNum;
    const totalActualCost = inputCost + outputCost;

    // Apply profit multiplier to get credit cost
    // Convert USD cost to credits (assuming 1 credit = $0.001, so 1 USD = 1000 credits)
    const creditCostPerDollar = 1000;
    const creditsToDeduct = Math.round(totalActualCost * creditCostPerDollar * profitMultiplier);

    console.log("=== AI Usage Credit Calculation ===");
    console.log(`Model: ${model}`);
    console.log(`Prompt Tokens: ${promptTokens}`);
    console.log(`Completion Tokens: ${completionTokens}`);
    console.log(`Prompt Price per Token: $${promptPriceNum.toFixed(10)}`);
    console.log(`Completion Price per Token: $${completionPriceNum.toFixed(10)}`);
    console.log(`Input Cost: $${inputCost.toFixed(6)}`);
    console.log(`Output Cost: $${outputCost.toFixed(6)}`);
    console.log(`Total Cost (Actual): $${totalActualCost.toFixed(6)}`);
    console.log(`Profit Multiplier: ${profitMultiplier}x`);
    console.log(`Credits to Deduct: ${creditsToDeduct}`);
    console.log("====================================");

    const currentCredits = await this.getOrCreateUserCredits();
    const currentBalance = parseInt(currentCredits.balance, 10) || 0;
    const hadEnoughBalance = currentBalance >= creditsToDeduct;
    const actualDeduction = Math.min(creditsToDeduct, currentBalance);

    if (actualDeduction <= 0) {
      return {
        creditsDeducted: 0,
        userCredits: currentCredits,
        hadEnoughBalance: false,
      };
    }

    const updatedCredits = await this.deductCredits(actualDeduction, "ai_chat_usage", {
      model,
      promptTokens,
      completionTokens,
      actualCostUSD: totalActualCost.toFixed(6),
      profitMultiplier,
      requestedCredits: creditsToDeduct,
    });

    return {
      creditsDeducted: actualDeduction,
      userCredits: updatedCredits,
      hadEnoughBalance,
    };
  }

  /**
   * Block AI usage when balance is zero or negative (call before streaming).
   */
  async assertPositiveBalanceForAi(): Promise<
    { ok: true; balance: number } | { ok: false; balance: number }
  > {
    const credits = await this.getOrCreateUserCredits();
    const balance = parseInt(credits.balance, 10) || 0;
    if (await this.hasUnlimitedAiCredits()) {
      return { ok: true, balance };
    }
    if (balance <= 0) return { ok: false, balance };
    return { ok: true, balance };
  }

  /**
   * Redeem a coupon via Supabase RPC (requires migration `redeem_coupon`).
   */
  async redeemCouponCode(rawCode: string): Promise<{
    ok: boolean;
    bonusCredits?: number;
    error?: string;
  }> {
    if (!isSupabaseConfigured) {
      return { ok: false, error: "not_configured" };
    }
    const trimmed = rawCode.trim();
    if (!trimmed) {
      return { ok: false, error: "empty_code" };
    }
    const { data, error } = await supabase.rpc("redeem_coupon", { p_raw_code: trimmed });
    if (error) {
      console.warn("[redeem_coupon]", error.message);
      return { ok: false, error: error.message };
    }
    const payload = data as { ok?: boolean; error?: string; bonus_credits?: number } | null;
    if (payload?.ok === true) {
      return { ok: true, bonusCredits: payload.bonus_credits };
    }
    return { ok: false, error: payload?.error || "unknown" };
  }

  private invalidateCreditQueries(queryClient?: QueryClient): void {
    queryClient?.invalidateQueries({ queryKey: ["/api/credits"] });
    queryClient?.invalidateQueries({ queryKey: ["credits-overview"] });
    queryClient?.invalidateQueries({ queryKey: ["credits-status"] });
    queryClient?.invalidateQueries({ queryKey: ["billing-subscription"] });
  }

  /**
   * Ensure welcome credits exist, then redeem pending signup coupon from localStorage (if any).
   * Waits for an auth session so `redeem_coupon` sees auth.uid(); retries briefly if session/RPC races.
   */
  async bootstrapRegistrationCredits(
    queryClient?: QueryClient
  ): Promise<{ couponApplied: boolean; bonusCredits?: number }> {
    if (typeof window === "undefined") {
      return { couponApplied: false };
    }

    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session?.user) {
      console.warn("[bootstrapRegistrationCredits] No auth session yet; coupon redeem skipped");
      return { couponApplied: false };
    }

    await this.getOrCreateUserCredits();

    const pending = localStorage.getItem(PENDING_SIGNUP_COUPON_KEY);
    if (!pending?.trim()) {
      return { couponApplied: false };
    }

    const maxAttempts = 4;
    let lastResult: { ok: boolean; bonusCredits?: number; error?: string } = { ok: false };

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      lastResult = await this.redeemCouponCode(pending.trim());
      if (lastResult.ok && lastResult.bonusCredits != null) {
        localStorage.removeItem(PENDING_SIGNUP_COUPON_KEY);
        this.invalidateCreditQueries(queryClient);
        return { couponApplied: true, bonusCredits: lastResult.bonusCredits };
      }

      const err = lastResult.error || "";
      const retryable =
        err === "not_authenticated" ||
        err === "no_credits_row" ||
        err.includes("JWT") ||
        err.includes("session");

      if (retryable && attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 250 * attempt));
        await supabase.auth.getSession();
        await this.getOrCreateUserCredits();
        continue;
      }

      console.warn("[bootstrapRegistrationCredits] Coupon redeem failed:", err || lastResult);
      break;
    }

    const err = lastResult.error || "";
    if (
      err === "invalid_code" ||
      err === "expired" ||
      err === "not_started" ||
      err === "fully_redeemed" ||
      err === "already_redeemed" ||
      err === "empty_code"
    ) {
      localStorage.removeItem(PENDING_SIGNUP_COUPON_KEY);
    }
    return { couponApplied: false };
  }

  async getTransactions(limit: number = 20): Promise<CreditTransaction[]> {
    const userId = await this.getCurrentUserId();
    
    const { data, error } = await supabase
      .from("credit_transactions")
      .select("id, user_id, type, amount, balance_before, balance_after, reason, metadata, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Error fetching transactions:", error);
      return [];
    }

    return (data || []).map((row: any) => this.mapTransactionFromDb(row));
  }

  async getCreditsOverview(): Promise<CreditsOverview> {
    const credits = await this.getOrCreateUserCredits();
    const transactions = await this.getTransactions(20);
    const unlimitedAi = await this.hasUnlimitedAiCredits();

    const balance = parseInt(credits.balance);
    const totalEarned = parseInt(credits.totalEarned);
    const totalSpent = parseInt(credits.totalSpent);
    const monthlyAllowance = totalEarned > 0 ? totalEarned : 1000;
    const percentageUsed = monthlyAllowance > 0 
      ? Math.min(100, Math.round((totalSpent / monthlyAllowance) * 100)) 
      : 0;

    const usageByCategory: Record<string, number> = {};
    const monthlyUsageMap: Record<string, number> = {};

    transactions.forEach((tx: CreditTransaction) => {
      if (tx.type === "debit") {
        const category = tx.reason || "other";
        const amount = Math.abs(parseInt(tx.amount));
        usageByCategory[category] = (usageByCategory[category] || 0) + amount;

        const date = new Date(tx.createdAt).toISOString().split("T")[0];
        monthlyUsageMap[date] = (monthlyUsageMap[date] || 0) + amount;
      }
    });

    const monthlyUsage = Object.entries(monthlyUsageMap)
      .map(([date, credits]) => ({ date, credits }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      balance,
      totalEarned,
      totalSpent,
      monthlyAllowance,
      percentageUsed,
      transactions,
      usageByCategory,
      monthlyUsage,
      isLowBalance: unlimitedAi ? false : balance < 100 && balance > 0,
      isDepleted: unlimitedAi ? false : balance <= 0,
      unlimitedAiCredits: unlimitedAi || undefined,
    };
  }

  async getCreditsStatus(): Promise<CreditsStatus> {
    const credits = await this.getOrCreateUserCredits();
    const balance = parseInt(credits.balance);
    const monthlyAllowance = parseInt(credits.totalEarned) || 1000;
    const unlimitedAi = await this.hasUnlimitedAiCredits();

    return {
      balance,
      monthlyAllowance,
      isLowBalance: unlimitedAi ? false : balance < 100 && balance > 0,
      isDepleted: unlimitedAi ? false : balance <= 0,
      unlimitedAiCredits: unlimitedAi || undefined,
    };
  }

  async getBillingSubscription(): Promise<BillingSubscription> {
    const userId = await this.getCurrentUserId();
    
    const { data: subscription, error: subError } = await supabase
      .from("user_subscriptions")
      .select("*, subscription_plans(*)")
      .eq("user_id", userId)
      .single();

    if (subError || !subscription) {
      return {
        tierName: "Free",
        monthlyCredits: 1000,
        nextBillingDate: null,
        isCancelled: false,
        cancelAtPeriodEnd: false,
        subscription: null,
        plan: {
          name: "free",
          displayName: "Free Plan",
          price: "0",
        },
      };
    }

    const plan = subscription.subscription_plans;
    
    return {
      tierName: plan?.display_name || plan?.name || "Free",
      monthlyCredits: parseInt(plan?.monthly_credits || "1000"),
      nextBillingDate: subscription.current_period_end,
      isCancelled: subscription.status === "cancelled",
      cancelAtPeriodEnd: subscription.cancel_at_period_end === "true",
      subscription: {
        status: subscription.status,
      },
      plan: plan ? {
        name: plan.name,
        displayName: plan.display_name,
        price: plan.price,
      } : null,
    };
  }

  async getBillingTiers(): Promise<BillingTiersResponse> {
    // Fetch subscriptions from DeeplySmartSubscriptions table
    // Note: Supabase converts camelCase to snake_case for column names
    const { data: subscriptions, error } = await supabase
      .from("DeeplySmartSubscriptions")
      .select("id, name, description, price, currency, interval, \"intervalCount\", \"trialDays\", \"maxDevices\", \"numberOfTokens\", status")
      .eq("status", "active")
      .order("price", { ascending: true });

    if (error) {
      console.error("Error fetching subscriptions:", error);
      // Return fallback tiers if there's an error
      return {
        tiers: [
          {
            name: "Free",
            price: 0,
            priceDisplay: "$0",
            monthlyCredits: 1000,
            features: ["1,000 monthly credits", "Basic AI chat", "Knowledge base", "Source management"],
            popular: false,
          },
        ],
      };
    }

    if (!subscriptions || subscriptions.length === 0) {
      return {
        tiers: [
          {
            name: "Free",
            price: 0,
            priceDisplay: "$0",
            monthlyCredits: 1000,
            features: ["1,000 monthly credits", "Basic AI chat", "Knowledge base", "Source management"],
            popular: false,
          },
        ],
      };
    }

    const tiers: PricingTier[] = subscriptions.map((sub: any, index: number) => {
      // Parse price - handle numeric type from database
      const price = typeof sub.price === 'number' ? sub.price : parseFloat(sub.price || '0');
      const interval = sub.interval || "month";
      // Handle both camelCase and snake_case for intervalCount
      const intervalCount = sub.intervalCount || sub.interval_count || 1;
      // numberOfTokens is the credits amount
      const numberOfTokens = parseInt(sub.numberOfTokens || sub.number_of_tokens || "0");
      const monthlyCredits = numberOfTokens; // Use numberOfTokens as monthlyCredits
      
      // Build features array from subscription data
      const features: string[] = [];
      if (numberOfTokens > 0) {
        features.push(`${formatCredits(numberOfTokens)} monthly credits`);
      }
      if (sub.maxDevices || sub.max_devices) {
        const maxDevices = sub.maxDevices || sub.max_devices || 1;
        if (maxDevices > 1) {
          features.push(`Up to ${maxDevices} devices`);
        }
      }
      if (sub.description) {
        // Split description by newlines or commas to create feature list
        const descFeatures = sub.description.split(/\n|,|;/).filter((f: string) => f.trim().length > 0);
        features.push(...descFeatures.map((f: string) => f.trim()));
      }
      
      // Default features if none provided
      if (features.length === 0) {
        features.push("Basic features");
      }
      
      // Format price display
      let priceDisplay = "$0";
      if (price > 0) {
        const currency = sub.currency || "USD";
        const currencySymbol = currency === "USD" ? "$" : currency;
        if (intervalCount > 1) {
          priceDisplay = `${currencySymbol}${price.toFixed(2)}/${intervalCount} ${interval}s`;
        } else {
          priceDisplay = `${currencySymbol}${price.toFixed(2)}/${interval}`;
        }
      }
      
      return {
        id: sub.id?.toString(),
        name: sub.name || "Plan",
        price: price,
        priceDisplay: priceDisplay,
        monthlyCredits: monthlyCredits,
        features: features,
        popular: index === 1, // Mark second tier as popular by default
        description: sub.description,
        currency: sub.currency || "USD",
        interval: interval,
        intervalCount: intervalCount,
        trialDays: sub.trialDays || sub.trial_days || 0,
        maxDevices: sub.maxDevices || sub.max_devices || 1,
        numberOfTokens: numberOfTokens,
        status: sub.status || "active",
      };
    });

    return { tiers };
  }

  /**
   * Initialize Stripe with test publishable key
   */
  private async getStripeInstance(): Promise<Stripe | null> {
    try {
      const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);
      return stripe;
    } catch (error) {
      console.error("Error loading Stripe:", error);
      return null;
    }
  }

  /**
   * Get Stripe Payment Link for a tier (client-side only approach)
   * Payment Links should be pre-created in Stripe Dashboard and stored in the database
   * or passed as a parameter. For now, we'll use a direct redirect approach.
   */
  async getPaymentLink(tierId: string, tier: PricingTier): Promise<string | null> {
    // Option 1: Payment Link stored in database (recommended)
    // Fetch from DeeplySmartSubscriptions table if it has a payment_link field
    const { data: subscription } = await supabase
      .from("DeeplySmartSubscriptions")
      .select("payment_link, stripe_price_id")
      .eq("id", tierId)
      .single();

    if (subscription?.payment_link) {
      return subscription.payment_link;
    }

    // Option 2: Use Stripe Price ID to create a payment link URL
    // This requires the price to be created in Stripe Dashboard
    if (subscription?.stripe_price_id) {
      // Redirect to Stripe Checkout with price ID
      // Note: This is a simplified approach - in production, use Payment Links
      const stripe = await this.getStripeInstance();
      if (stripe) {
        // For client-side only, we'll return a payment link format
        // You'll need to create these in Stripe Dashboard
        return null; // Will trigger payment modal instead
      }
    }

    return null;
  }

  /**
   * Process payment and update credits after successful Stripe payment
   * Works for both Payment Links redirects and direct modal payments
   */
  async processPaymentSuccess(
    sessionId: string,
    tierId: string,
    creditsAmount: number
  ): Promise<void> {
    try {
      const userId = await this.getCurrentUserId();
      const credits = await this.getOrCreateUserCredits();
      const currentBalance = parseInt(credits.balance);
      const newBalance = currentBalance + creditsAmount;
      const newTotalEarned = parseInt(credits.totalEarned) + creditsAmount;

      // Update user credits
      const { error: updateError } = await supabase
        .from("user_credits")
        .update({
          balance: String(newBalance),
          total_earned: String(newTotalEarned),
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", userId);

      if (updateError) {
        console.error("Error updating credits:", updateError);
        throw new Error(`Failed to update credits: ${updateError.message}`);
      }

      // Record transaction in credit_transactions table
      await supabase.from("credit_transactions").insert({
        user_id: userId,
        type: "credit",
        amount: String(creditsAmount),
        balance_before: String(currentBalance),
        balance_after: String(newBalance),
        reason: "subscription_purchase",
        metadata: {
          sessionId,
          tierId,
          source: "stripe",
        },
      });
    } catch (error) {
      console.error("Error processing payment success:", error);
      throw error;
    }
  }

  /**
   * Create Stripe Checkout Session via API
   */
  private async createStripeCheckoutSession(tier: PricingTier): Promise<string> {
    const userId = await this.getCurrentUserId();
    
    // Get user email from centralized session
    const session = await getCurrentSession();
    const customerEmail = session?.user?.email || undefined;

    const response = await fetch('/api/stripe/checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tierId: tier.id,
        userId: userId,
        amount: tier.price,
        currency: (tier.currency || 'USD').toLowerCase(),
        numberOfTokens: tier.numberOfTokens || tier.monthlyCredits,
        planName: tier.name,
        customerEmail: customerEmail,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create checkout session');
    }

    const data = await response.json();
    if (!data.url) {
      throw new Error('No checkout URL returned from server');
    }

    return data.url;
  }

  /**
   * Handle subscription upgrade with Stripe payment
   * Returns payment link URL or null if payment modal should be used
   */
  async upgradeSubscription(tier: PricingTier): Promise<{ type: 'redirect' | 'modal', url?: string }> {
    if (!tier.id) {
      throw new Error("Tier ID is required for subscription upgrade");
    }

    if (tier.price === 0) {
      // Free tier - just update credits without payment
      const creditsAmount = tier.numberOfTokens || tier.monthlyCredits;
      await this.processPaymentSuccess("free", tier.id, creditsAmount);
      return { type: 'redirect' };
    }

    try {
      // For Beast tier, redirect to contact sales
      if (tier.name === "Beast") {
        window.location.href = "mailto:support@deeplysmart.io?subject=Beast Plan Inquiry";
        return { type: 'redirect' };
      }

      // Try to get payment link from database first (for pre-configured payment links)
      const paymentLink = await this.getPaymentLink(tier.id, tier);
      
      if (paymentLink) {
        // Redirect to pre-created Stripe Payment Link
        const creditsAmount = tier.numberOfTokens || tier.monthlyCredits;
        const successUrl = `${window.location.origin}/pricing?success=true&tier_id=${tier.id}&credits_amount=${creditsAmount}`;
        const cancelUrl = `${window.location.origin}/pricing?canceled=true`;
        const linkWithParams = `${paymentLink}${paymentLink.includes('?') ? '&' : '?'}success_url=${encodeURIComponent(successUrl)}&cancel_url=${encodeURIComponent(cancelUrl)}`;
        return { type: 'redirect', url: linkWithParams };
      }

      // Create Stripe Checkout Session via API
      try {
        const checkoutUrl = await this.createStripeCheckoutSession(tier);
        return { type: 'redirect', url: checkoutUrl };
      } catch (apiError: any) {
        console.warn("Failed to create checkout session via API, falling back to modal:", apiError);
        // If API fails, fall back to payment modal
        return { type: 'modal' };
      }
    } catch (error: any) {
      console.error("Error upgrading subscription:", error);
      throw error;
    }
  }
}

export const creditsService = new CreditsService();

/** React Query fetcher — loads credits via Supabase, not HTTP GET /api/credits. */
export function creditsQueryFn() {
  return creditsService.getOrCreateUserCredits();
}
